import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import __ from "lodash";
import axios from "axios";
import MaterialTable from "@material-table/core";
import {TablePagination, Checkbox, Select, MenuItem, IconButton, useTheme} from "@material-ui/core";
import {ListAltOutlined, ListAltTwoTone} from '@material-ui/icons';

import {ReactComponent as PdfLogo} from "../../../assets/pdf.svg";
import {ReactComponent as DocxLogo} from "../../../assets/docx.svg";
import {ReactComponent as CovertToDocLogo} from "../../../assets/ContracToWordDoc.svg";
import {getApiSitePrefix} from "../../../functions/SiteDefaults";
import SystemContext from "../../../SystemContext";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import ContractsValidator from "../validators/ContractsValidator";
import CompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";
import DocumentTemplateLookup from "../../Settings/components/DocumentTemplateLookup";

import { useContracts, useAddContract, useUpdateContract, useDeleteContract } from "../../../services/contracts";
import {
  MaskedText,
  DatePicker, Alert,
} from "../../../controls";
import Contract from "./Contract";
import { defaultComponents } from '../../../controls/Material-Table-Components';

const apiSitePrefix = getApiSitePrefix();
const dateFormat = "DD/MM/YYYY";
const today = moment().toISOString();

const ContractList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const validatorColumn = ValidatorColumn(ContractsValidator, true);

  const props = {
    ..._props
  }
  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });
  const [alert, setAlert] = useState({
    open: false,
    severity: "error",
    message: ""
  });

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddContract = useAddContract();
  const handleUpdateContract = useUpdateContract();
  const handleDeleteContract = useDeleteContract();

  const queryClient = useQueryClient();
  const extraKeys = [];

  const contracts = useContracts({tableRef, queryClient, extraKeys});


  const handleContractContentUpdates = async (oldData, contractJson) => {
    if (contractJson) {
      const newData = {...oldData, contractContents: contractJson};
      await handleUpdateContract.mutate({record: {...newData}});
    }
  }

  const duplicateContract = async (sourceContract) => {
    if (sourceContract) {
      let newData = __.omit(sourceContract, ['contractUuid', 'contractStartDate', 'contractEndDate', 'contractReference', 'isTemplate', 'active']);
      newData = {...newData, contractName: `COPY OF - ${sourceContract.contractName}`, active: false, isTemplate: false};
      await handleAddContract.mutate({record: newData});
    }
  }

  const downloadContract = (contractFile) => {
    window.open(contractFile, "_blank");
  }

  const regenerateContractFromTemplate = async (contractUuid) => {
    SystemData.confirm(
      "Re-generate Contract",
      "Are you sure you wish to Re-generate the contract document from the template?",
      [
        {icon: "check_circle", value: true, label: "Yes", options: {color:"primary"}},
        {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
      ],
      async (response) => {
        if (response) {
          await generateContractFromTemplate(contractUuid);
        }
      }
    );
  }

  const generateContractFromTemplate = async (contractUuid) => {
    const headers = {...axios.defaults.headers, 'Content-Type': 'application/json'};

    try {
      const response = await axios.post(
        `${apiSitePrefix}/contracts/generate/${contractUuid}`,
        {},
        {headers,}
      );

      //console.error(response);
      if (response && response.data) {
        if (response.data.status) {
          setAlert({ severity: "info", open: true, message: "Contract successfully generated", timeOut: 10 });
          queryClient.invalidateQueries(['Contracts', ...extraKeys]);
        } else {
          setAlert({ severity: "error", open: true, message: "Contract generation from template failed with this error: " + response.data.error });
        }
      } else {
        setAlert({ severity: "error", open: true, message: "Contract generation from template failed with an unknown error " });
      }
    } catch (err) {
      console.error(err);
      setAlert({ severity: "error", open: true, message: "Contract generation from template failed with this error: " + err.message });
    }
  }

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize:12, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={
      "U": "[A-Z]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    const comboValues = [{"code":"CHARTER","value":"Charter"},{"code":"SGHA","value":"Standard Ground Handling Agreement"},{"code":"OTHER","value":"Generic Agreement"}];

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...(ColumnWidth(275)),
        title: "Contract Name",
        field: "contractName",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(275)),
        title: "Contract Type",
        field: "contractType",
        editable: state.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "",
        editComponent: (props) => {
          return (
            <Select
              value={props.value == null?'':props.value}
              onChange={
                (evt, data) => {
                  if (data.props && data.props.children && Array.isArray(data.props.children)) {
                    const typeCode = data.props.children[0];
                    props.onChange(typeCode);
                  }
                }
              }
              renderValue={(value) => {
                const valueIndex = __.findIndex(comboValues, {code: value});
                return(`${valueIndex < 0 ? '' : comboValues[valueIndex].value}`);
              }}
            >
              {(comboValues) ?
                comboValues.map((record) => {
                  return (
                    <MenuItem key={record.code} value={record.value}>{record.code} - {record.value}</MenuItem>
                  );
                }) :
                null
              }
            </Select>
          )
        },
        render: rowData => {
          const valueIndex = __.findIndex(comboValues, {code: rowData.contractType});
          return(<div>{valueIndex < 0 ? '' : comboValues[valueIndex].value}</div>);
        }
      },
      {
        ...baseColumn,
        title: "Revision",
        field: "contractRevision",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(10)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        title: "Contracted Party",
        field: "company.companyName",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <CompanyUuidLookup
            companyType={props.rowData.contractType === "CHARTER" ? "CLIENT" : (props.rowData.contractType === "SGHA" ? "SUPPLIER" : "ANY")}
            value={props.value}
            returnType={"object"}
            onChange={(returnObject) => {
              if (returnObject.found) {
                const contractedParty = returnObject.data
                const data = {
                  ...props.rowData,
                  "company.companyName": contractedParty.companyName,
                  contractedPartyUuid: contractedParty.companyUuid,
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
      },
      {
        ...baseColumn,
        title: "Reference",
        field: "contractReference",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(50)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(120)),
        title: "Start Date",
        field: "contractStartDate",
        type: "date",
        //emptyValue: today,
        //initialEditValue:today,
        editComponent: (props) => (
          <DatePicker
            // minDate={today}
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(120)),
        title: "End Date",
        field: "contractEndDate",
        type: "date",
        //emptyValue: today,
        //initialEditValue:today,
        editComponent: (props) => (
          <DatePicker
            // minDate={today}
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        title: "Template Document",
        field: "documentTemplate.templateName",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <DocumentTemplateLookup
            value={props.value}
            returnType={"object"}
            onChange={(returnObject) => {
              if (returnObject.found) {
                const template = returnObject.data;
                const data = {
                  ...__.omit(props.rowData, [template.documentTemplate]),
                  "documentTemplate.templateName": template.templateName,
                  documentTemplateUuid: template.templateUuid,
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(70, undefined, undefined, {textAlign: 'center'}, {textAlign: 'center'})),
        title: "Template",
        field: "isTemplate",
        type: "boolean",
        sorting: false,
        emptyValue: false,
        initialEditValue: false,
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.isTemplate === true} value="true" />
      },
      {
        ...baseColumn,
        ...(ColumnWidth(70, undefined, undefined, {textAlign: 'center'}, {textAlign: 'center'})),
        title: "Active",
        field: "active",
        type: "boolean",
        sorting: false,
        emptyValue: false,
        initialEditValue: false,
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.active === true} value="true" />
      },
    ];
  };

  const padding = "dense";

  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <Alert
        onChange={(open) => {
          setAlert({ ...alert, open })
        }}
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        timeOut={alert.timeOut || 0}
      />
      <MaterialTable
        tableRef={tableRef}
        style={{
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "2px",
          },
          toolbarButtonAlignment: "right",
          pageSize: 15,
          detailPanelType: "single",
          padding: padding,
        }}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
        }}
        actions={[
          (rowData) => (
            {
              hidden: rowData.generatedContractDoc,
              disabled: !rowData.active,
              icon: () => {
                return(
                  <IconButton>
                    <CovertToDocLogo width={28} height={28} style={{width: 28, height:28,  }}/>
                  </IconButton>
                )
              },
              tooltip: "Generate Contract from Template",
              isFreeAction: false,
              onClick: (e, rowData) => {
                generateContractFromTemplate(rowData.contractUuid);
              }
            }
          ),
          (rowData) => (
            {
              hidden: !rowData.generatedContractDoc,
              disabled: !rowData.active || !rowData.generatedContractDoc,
              icon: () => {
                return(
                  <IconButton>
                    <DocxLogo width={22} height={22} style={{width: 22, height:22, fill: (rowData.active && rowData.generatedContractDoc ? '#1143c2' : '#b3b0b0') }}/>
                  </IconButton>
                )
              },
              tooltip: "Download Contract",
              isFreeAction: false,
              onClick: (e, rowData) => {
                downloadContract(rowData.generatedContractDoc);
              }
            }
          ),
          (rowData) => (
            {
              hidden: !rowData.generatedContractDoc,
              disabled: !rowData.active || !rowData.generatedContractDoc,
              icon: "cached",
              tooltip: "Re-Generate Contract from Template",
              isFreeAction: false,
              onClick: (e, rowData) => {
                regenerateContractFromTemplate(rowData.contractUuid);
              }
            }
          ),
          (rowData) => (
            {
              hidden: false,
              disabled: !rowData.active,
              icon: 'file_copy',
              iconProps: { style: (rowData.active) ? { color: theme.palette.primary.main } : {color: '#b3b0b0'}},
              tooltip: "Duplicate Contract",
              isFreeAction: false,
              onClick: (e, rowData) => {
                duplicateContract(rowData).then(r => {});
              }
            }
          ),
        ]}

        title="Contract Management"
        columns={getColumns({canEdit: state.canEdit})}
        detailPanel={
          [
            {
              tooltip: "Edit Contract",
              icon: () => { return( <ListAltTwoTone /> ) },
              openIcon: () => { return( <ListAltOutlined /> ) },
              render: (rowData) => {
                return (
                  <div>
                    <div style={{ backgroundColor: "#f2f2f2"}}>
                      <Contract
                        canEdit={state.canEdit}
                        record={rowData}
                        saveContract={async (contractJson) => {
                          await handleContractContentUpdates(rowData, contractJson);
                        }}
                      />
                    </div>
                  </div>
                );
              },
            },
          ]
        }
        data={contracts.data}
        editable={{
          isEditable: () => {
            return state.canEdit;
          },
          isDeletable: () => {
            return state.canEdit;
          },
          onRowAdd: state.canEdit
            ? (newData) => new Promise((resolve, reject) => {
              handleAddContract.mutate({record: newData, resolve, reject, extraKeys});
            })
            : undefined,
          onRowUpdate: (newData, oldData) =>
            new Promise((resolve, reject) => {
              handleUpdateContract.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
            }),
          onRowDelete: (oldData) =>
            new Promise((resolve, reject) => {
              handleDeleteContract.mutate({oldRecord: oldData, resolve, reject, extraKeys});
            }),
        }}
      />
    </div>
  );

}

export default ContractList;
